import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UnitToggle } from '@/components/UnitToggle';
import { motion } from 'framer-motion';
import {
  resolveUnits, weightUnit, windUnit, tempUnit, displayWeight, weightToKg,
  windToKnots, tempToC, weightPlaceholder, windPlaceholder, tempPlaceholder,
  displayWind, displayTemp, fmtBoardDims,
} from '@/lib/units';
import {
  Wind, Gauge, Waves, Thermometer, MapPin, Sparkles, Loader2, Compass,
  ShieldCheck, Lightbulb, Wand2, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const DIRS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const RIDING_STYLE_KEYS = ['freeride', 'freestyle', 'wave', 'big_air', 'foil'];
const WATER_STATE_KEYS = ['flat', 'choppy', 'waves'];
const LEVELS = ['beginner', 'intermediate', 'advanced'];

const InfoBlock = ({ icon: Icon, label, value, accent = '#00B4D8', children }) => (
  <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-4">
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="w-4 h-4" style={{ color: accent }} /><span className="label-eyebrow">{label}</span></div>
    {value && <div className="font-data text-3xl font-semibold mt-2" style={{ color: accent }}>{value}</div>}
    {children}
  </div>
);

export default function GearAdvisor() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [units, setUnits] = useState(() => resolveUnits(user));

  const [spots, setSpots] = useState([]);
  const [spotId, setSpotId] = useState('');
  const [windVal, setWindVal] = useState('');
  const [dir, setDir] = useState('');
  const [weightVal, setWeightVal] = useState(() => { const v = displayWeight(user?.weight_kg, resolveUnits(user)); return v == null ? '' : String(v); });
  const [level, setLevel] = useState(user?.skill_level || 'intermediate');
  const [style, setStyle] = useState('freeride');
  const [waterState, setWaterState] = useState('choppy');
  const [tempVal, setTempVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get('/spots').then(({ data }) => setSpots(data)).catch(() => {}); }, []);

  // Switching units keeps the physical value the same by converting the entered numbers.
  const onUnitsChange = (next) => {
    if (next === units) return;
    if (windVal !== '') { const v = displayWind(windToKnots(windVal, units), next); setWindVal(v == null ? '' : String(v)); }
    if (weightVal !== '') { const v = displayWeight(weightToKg(weightVal, units), next); setWeightVal(v == null ? '' : String(v)); }
    if (tempVal !== '') { const v = displayTemp(tempToC(tempVal, units), next); setTempVal(v == null ? '' : String(v)); }
    setUnits(next);
    if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', next);
  };

  const submit = async () => {
    if (!windVal) { toast.error(t('gear.enterWind')); return; }
    const spot = spots.find((s) => s.id === spotId);
    const payload = {
      spot_id: spotId || null,
      location: spot ? `${spot.name}, ${spot.country}` : null,
      wind_knots: windToKnots(windVal, units),
      wind_direction: dir || null,
      weight_kg: weightToKg(weightVal, units),
      skill_level: level,
      riding_style: style,
      water_state: waterState,
      water_temp_c: tempVal !== '' ? tempToC(tempVal, units) : null,
      lang: (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2),
    };

    setLoading(true); setResult(null);
    const MAX_TRIES = 3;
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_TRIES; attempt += 1) {
      try {
        const { data } = await api.post('/gear/advice', payload, { timeout: 45000 });
        setResult(data.advice);
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // Retry transient gateway/AI blips (no response, 429, or 5xx) — these are
        // the intermittent "502" errors. Client (4xx) errors are not retried.
        const transient = !err.response || status === 429 || (status >= 500 && status < 600);
        if (attempt < MAX_TRIES && transient) {
          await new Promise((r) => setTimeout(r, 900 * attempt));
          continue;
        }
        break;
      }
    }
    if (lastErr) {
      const status = lastErr?.response?.status;
      // For transient/server errors show the friendly localized message instead of a raw code.
      const transient = !lastErr.response || status === 429 || (status >= 500 && status < 600);
      toast.error(transient ? t('gear.adviceError') : apiError(lastErr, t('gear.adviceError')));
    }
    setLoading(false);
  };

  // ---- Free for everyone (incl. anonymous visitors) ----
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-2"><Wand2 className="w-4 h-4" /> {t('gear.proEyebrow')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('gear.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('gear.subtitle')}</p>
        </div>
        <div className="shrink-0" data-testid="gear-units">
          <Label className="text-xs text-muted-foreground mb-1.5 block">{t('gear.unitSystem')}</Label>
          <UnitToggle value={units} onChange={onUnitsChange} testid="gear-unit-toggle" compact />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-8">
        {/* FORM */}
        <div className="lg:col-span-2 kite-card p-5 space-y-4 h-fit" data-testid="gear-advisor-form">
          <div>
            <Label className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#48CAE4]" /> {t('gear.spot')} <span className="text-xs text-muted-foreground">{t('gear.optional')}</span></Label>
            <Select value={spotId} onValueChange={setSpotId}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-spot-select"><SelectValue placeholder={t('gear.spotPlaceholder')} /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-64">
                {spots.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.country}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5"><Wind className="w-3.5 h-3.5 text-[#00B4D8]" /> {t('gear.wind', { unit: windUnit(units) })}</Label>
              <Input type="number" inputMode="decimal" value={windVal} onChange={(e) => setWindVal(e.target.value)} placeholder={windPlaceholder(units)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-wind" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Compass className="w-3.5 h-3.5 text-[#48CAE4]" /> {t('gear.direction')}</Label>
              <Select value={dir} onValueChange={setDir}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-dir"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-64">{DIRS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-[#2EE59D]" /> {t('gear.weight', { unit: weightUnit(units) })}</Label>
              <Input type="number" inputMode="decimal" value={weightVal} onChange={(e) => setWeightVal(e.target.value)} placeholder={weightPlaceholder(units)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-weight" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Thermometer className="w-3.5 h-3.5 text-[#FFB020]" /> {t('gear.water', { unit: tempUnit(units) })}</Label>
              <Input type="number" inputMode="decimal" value={tempVal} onChange={(e) => setTempVal(e.target.value)} placeholder={tempPlaceholder(units)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-temp" />
            </div>
          </div>
          <div>
            <Label>{t('gear.skillLevel')}</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-level"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`levels.${l}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('gear.ridingStyle')}</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-style"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white">{RIDING_STYLE_KEYS.map((k) => <SelectItem key={k} value={k}>{t(`gear.ridingStyles.${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Waves className="w-3.5 h-3.5 text-[#48CAE4]" /> {t('gear.waterState')}</Label>
            <Select value={waterState} onValueChange={setWaterState}>
              <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="gear-water-state"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white">{WATER_STATE_KEYS.map((k) => <SelectItem key={k} value={k}>{t(`gear.waterStates.${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full h-12 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="gear-advisor-submit">
            {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('gear.analyzing')}</> : <><Sparkles className="w-5 h-5 mr-2" /> {t('gear.getAdvice')}</>}
          </Button>
        </div>

        {/* RESULTS */}
        <div className="lg:col-span-3">
          {!result && !loading && (
            <div className="kite-card p-10 text-center h-full flex flex-col items-center justify-center" data-testid="gear-advisor-empty">
              <div className="w-16 h-16 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center"><Wand2 className="w-8 h-8 text-[#00B4D8]" /></div>
              <h3 className="font-display text-2xl mt-5">{t('gear.emptyTitle')}</h3>
              <p className="text-muted-foreground mt-2 max-w-sm">{t('gear.emptyDesc')}</p>
            </div>
          )}

          {loading && (
            <div className="kite-card p-10 text-center h-full flex flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#00B4D8] animate-spin" />
              <h3 className="font-display text-2xl mt-5">{t('gear.loadingTitle')}</h3>
              <p className="text-muted-foreground mt-2">{t('gear.loadingDesc')}</p>
            </div>
          )}

          {result && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-5" data-testid="gear-advisor-result">
              <div className="kite-card p-5">
                <h3 className="font-semibold text-lg">{result.summary}</h3>
                {result.powered_assessment && <p className="text-sm text-muted-foreground mt-2">{result.powered_assessment}</p>}
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <InfoBlock icon={Wind} label={t('gear.kiteSize')} value={`${result.kite?.primary_size_m2}m²`} accent="#00B4D8">
                  {result.kite?.range_m2 && <div className="text-xs text-muted-foreground mt-1">{t('gear.range', { range: result.kite.range_m2 })}</div>}
                  {result.alt_kite_size_m2 && <div className="text-xs text-[#48CAE4] mt-1">{t('gear.backup', { size: result.alt_kite_size_m2 })}</div>}
                  {result.kite?.type && <div className="text-xs text-white/70 mt-2">{result.kite.type}</div>}
                </InfoBlock>
                <InfoBlock icon={Waves} label={t('gear.board')} value={null} accent="#48CAE4">
                  <div className="font-data text-xl font-semibold mt-2 text-[#48CAE4]">{result.board?.type}</div>
                  {fmtBoardDims(result.board, units) && (
                    <div className="text-xs mt-1.5">
                      <span className="label-eyebrow text-muted-foreground">{t('gear.boardDims')}</span>
                      <div className="font-data text-sm text-white/90 mt-0.5">{fmtBoardDims(result.board, units)}</div>
                    </div>
                  )}
                  {result.board?.note && <div className="text-xs text-white/70 mt-2">{result.board.note}</div>}
                </InfoBlock>
                <InfoBlock icon={Thermometer} label={t('gear.wetsuit')} value={null} accent="#FFB020">
                  <div className="font-data text-xl font-semibold mt-2 text-[#FFB020]">{result.wetsuit?.thickness_mm}</div>
                  {result.wetsuit?.type && <div className="text-xs text-muted-foreground mt-1">{result.wetsuit.type}</div>}
                  {result.wetsuit?.accessories?.length > 0 && <div className="text-xs text-white/70 mt-2">+ {result.wetsuit.accessories.join(', ')}</div>}
                </InfoBlock>
              </div>

              {result.kite?.note && (
                <div className="kite-card p-4 text-sm text-white/80"><span className="text-[#00B4D8] font-semibold">{t('gear.kiteNote')}</span>{result.kite.note}</div>
              )}

              {result.tips?.length > 0 && (
                <div className="kite-card p-5">
                  <h4 className="font-semibold flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-[#FFB020]" /> {t('gear.proTips')}</h4>
                  <ul className="space-y-2">{result.tips.map((tip, i) => <li key={i} className="text-sm text-white/85 flex items-start gap-2"><ArrowRight className="w-4 h-4 text-[#48CAE4] mt-0.5 shrink-0" /> {tip}</li>)}</ul>
                </div>
              )}

              {result.safety_notes?.length > 0 && (
                <div className="rounded-2xl border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-5">
                  <h4 className="font-semibold flex items-center gap-2 mb-3 text-[#FF6B35]"><ShieldCheck className="w-4 h-4" /> {t('gear.safety')}</h4>
                  <ul className="space-y-2">{result.safety_notes.map((s, i) => <li key={i} className="text-sm text-white/85 flex items-start gap-2"><span className="text-[#FF6B35] mt-1">•</span> {s}</li>)}</ul>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
